// Tendência de sono — as contas que a tela precisava e não tinha, puras.
//
// ═════════════════════════════════════════════════════════════════════════
// 🔴 A REGRA QUE ESTE ARQUIVO EXISTE PARA SUSTENTAR
// ═════════════════════════════════════════════════════════════════════════
// "Um score diário não significa nada; uma tendência de 5 dias significa
// tudo." A tela fazia o contrário: o 92 do dia dominava a dobra e a média de
// 5,8h aparecia dentro de um parágrafo. A pesquisa tem nome para o efeito —
// ORTOSSONIA: a nota diária piora o sono de quem a lê.
//
// O score FICA. O que muda é quem manda na hierarquia: a tendência. Para ela
// mandar, ela precisa existir como NÚMERO, e é isso que está aqui.
//
// ⚠️ TODA JANELA É EXPLÍCITA E VOLTA NO RESULTADO (`nights`, `days`). O defeito
// que motivou metade deste arquivo era um "-15h28min deficit vs 8h target" em
// vermelho gigante SEM dizer de que período — número sem denominador. Quem
// consome estas funções recebe o denominador junto e não tem como esquecer de
// mostrá-lo.
//
// PURO: sem React, sem Supabase, `now` por parâmetro — mesma disciplina de
// lib/health/recovery.ts e do engine de analytics.

import { sleepDurationValue, sleepScoreValue } from "./values";

/** Uma noite, no formato que `sleep_logs` guarda. */
export interface TrendNight {
  /** coluna DATE `sleep_date`, "YYYY-MM-DD". */
  date: string;
  /** hora do dia "HH:MM" ou "HH:MM:SS". */
  bedtime: string | null;
  wakeup: string | null;
  durationHours: number | null;
  score: number | null;
}

export interface WindowStats {
  /** Dias da janela pedida; os denominadores observados vêm por métrica. */
  days: number;
  /** Noites com registro dentro dela. Zero = a janela está vazia. */
  nights: number;
  /** Noites com cada métrica válida; podem ter denominadores distintos. */
  durationNights: number;
  scoreNights: number;
  avgDuration: number | null;
  avgScore: number | null;
}

export interface DebtResult {
  days: number;
  nights: number;
  /** Positivo = dívida; negativo = crédito. null = sem duração ou alvo válido. */
  minutes: number | null;
}

export interface ConsistencyResult {
  days: number;
  nights: number;
  /** Desvio-padrão da hora de deitar, em HORAS. null = noites de menos. */
  bedtimeStdDev: number | null;
  /** Desvio-padrão da hora de acordar, em HORAS. */
  wakeupStdDev: number | null;
}

export interface HrvTrendResult {
  recentDays: number;
  baselineDays: number;
  recentMedian: number | null;
  baselineMedian: number | null;
  /** Variação % do recente contra a base. null quando falta um dos lados. */
  deltaPct: number | null;
}

/** Noites mínimas para um desvio-padrão significar alguma coisa. */
export const MIN_CONSISTENCY_NIGHTS = 3;

/**
 * A hora de dormir numa reta contínua.
 *
 * 23:30 e 00:30 estão a UMA hora de distância, mas como número de relógio são
 * 23,5 e 0,5 — 23 horas de distância, e o desvio-padrão explodiria. A
 * convenção (antes inline em useSleep, agora aqui, uma definição só): hora
 * antes do meio-dia pertence à madrugada seguinte, então soma 24.
 */
export function sleepHourOf(hhmm: string): number | null {
  const hm = parseClock(hhmm);
  if (hm === null) return null;
  return hm.h < 12 ? hm.h + 24 + hm.m / 60 : hm.h + hm.m / 60;
}

/** Hora de ACORDAR na reta: já é de manhã, então não desloca. */
export function wakeHourOf(hhmm: string): number | null {
  const hm = parseClock(hhmm);
  return hm === null ? null : hm.h + hm.m / 60;
}

/**
 * "HH:MM[:SS]" → {h, m}, ou null.
 *
 * ⚠️ A CHECAGEM DE VAZIO É EXPLÍCITA, e ela nasceu de um defeito que o teste
 * pegou antes do commit: `Number("")` é 0, não NaN. Sem esta guarda, uma
 * coluna `wakeup` vazia entrava na conta como MEIA-NOITE e virava um outlier
 * de 7 horas dentro do desvio-padrão de consistência — o número ficaria pior
 * exatamente para quem tem noite sem hora registrada.
 */
function parseClock(hhmm: string): { h: number; m: number } | null {
  if (typeof hhmm !== "string" || hhmm.trim() === "") return null;
  const parts = hhmm.split(":");
  if (parts[0] === undefined || parts[0].trim() === "") return null;
  const h = Number(parts[0]);
  const rawM = parts[1];
  const m = rawM === undefined || rawM.trim() === "" ? 0 : Number(rawM);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return { h, m };
}

export function stdDev(values: number[]): number | null {
  if (values.length < 2) return null;
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const sq = values.reduce((s, v) => s + (v - mean) ** 2, 0) / values.length;
  return Math.sqrt(sq);
}

/**
 * As noites dentro dos últimos `days` dias LOCAIS, contando hoje.
 *
 * `sleep_date` é coluna DATE; parse ao MEIO-DIA local para o bucket de dia
 * nunca escorregar pela fronteira de UTC — mesma regra que a migration de
 * streak e o extractor do dashboard já usam.
 */
export function nightsInWindow(nights: TrendNight[], days: number, now: number): TrendNight[] {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - (days - 1));
  const from = start.getTime();
  return nights.filter((n) => {
    const t = Date.parse(n.date + "T12:00:00");
    return Number.isFinite(t) && t >= from && t <= now + 12 * 3_600_000;
  });
}

/**
 * A janela ANTERIOR de mesmo tamanho: [now-2·days+1, now-days].
 *
 * Existe para a direção da tendência não ser derivada por subtração de médias
 * ponderadas dentro do componente — conta que estava escrita inline, era
 * ilegível e não tinha teste. Aqui ela é uma janela como qualquer outra.
 */
export function previousWindowStats(nights: TrendNight[], days: number, now: number): WindowStats {
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  end.setDate(end.getDate() - days);
  return windowStats(nights, days, end.getTime());
}

/** Variação % entre duas janelas do mesmo tamanho. null = falta um dos lados. */
export function deltaPct(current: number | null, previous: number | null): number | null {
  if (current === null || previous === null || previous === 0) return null;
  return Math.round(((current - previous) / previous) * 1000) / 10;
}

export function windowStats(nights: TrendNight[], days: number, now: number): WindowStats {
  const inWin = nightsInWindow(nights, days, now);
  const durations = inWin.map((n) => sleepDurationValue(n.durationHours)).filter((v): v is number => v !== null);
  const scores = inWin
    .map((n) => sleepScoreValue(n.score))
    .filter((v): v is number => v !== null);
  const avg = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);
  const d = avg(durations);
  const s = avg(scores);
  return {
    days,
    nights: inWin.length,
    durationNights: durations.length,
    scoreNights: scores.length,
    avgDuration: d === null ? null : Math.round(d * 10) / 10,
    avgScore: s === null ? null : Math.round(s),
  };
}

/**
 * Dívida de sono COM A JANELA JUNTO.
 *
 * Só soma noites COM registro — noite não registrada não é noite sem dormir.
 * É a mesma regra do cálculo que já existia em useSleep; o que muda é que
 * agora `days` e `nights` saem no resultado, e a tela é obrigada a poder
 * dizer "nos últimos 7 dias, sobre 5 noites registradas".
 */
export function sleepDebt(
  nights: TrendNight[],
  targetHours: number,
  days: number,
  now: number,
): DebtResult {
  const durations = nightsInWindow(nights, days, now)
    .map(n => sleepDurationValue(n.durationHours))
    .filter((value): value is number => value !== null);
  const target = sleepDurationValue(targetHours);
  const minutes = durations.length === 0 || target === null || target === 0
    ? null
    : Math.round(durations.reduce((sum, duration) => sum + (target - duration) * 60, 0));
  return { days, nights: durations.length, minutes };
}

/**
 * 🔴 CONSISTÊNCIA DE HORÁRIO — a métrica de custo zero.
 *
 * `bedtime` e `wakeup` JÁ são gravados em toda noite. O desvio-padrão da hora
 * de deitar inclusive JÁ era calculado em useSleep (`stats.consistency`) —
 * e jogado fora: nenhuma tela o renderizava. Aqui ele volta, com a metade que
 * faltava (a hora de ACORDAR) e com a janela declarada.
 *
 * Por que importa: a pesquisa de 2026 põe QUANDO se dorme quase no mesmo peso
 * que QUANTO, e liga padrão irregular a transtorno de humor e declínio
 * cognitivo. Nenhum concorrente direto mostra isso bem.
 */
export function scheduleConsistency(
  nights: TrendNight[],
  days: number,
  now: number,
): ConsistencyResult {
  const inWin = nightsInWindow(nights, days, now);
  const beds = inWin
    .map((n) => (n.bedtime ? sleepHourOf(n.bedtime.slice(0, 5)) : null))
    .filter((v): v is number => v !== null);
  const wakes = inWin
    .map((n) => (n.wakeup ? wakeHourOf(n.wakeup.slice(0, 5)) : null))
    .filter((v): v is number => v !== null);

  const enough = (xs: number[]) => xs.length >= MIN_CONSISTENCY_NIGHTS;
  return {
    days,
    nights: inWin.length,
    bedtimeStdDev: enough(beds) ? stdDev(beds) : null,
    wakeupStdDev: enough(wakes) ? stdDev(wakes) : null,
  };
}

/**
 * Tendência de HRV — o recente contra a base, em %.
 *
 * Mediana dos dois lados: uma noite ruim não vira "tendência de queda". A
 * janela de base NÃO inclui a recente, senão o recente se compara consigo
 * mesmo e a variação encolhe sozinha.
 */
export function hrvTrend(
  points: Array<{ t: number; v: number }>,
  recentDays: number,
  baselineDays: number,
  now: number,
): HrvTrendResult {
  const dayMs = 86_400_000;
  const recentFrom = now - recentDays * dayMs;
  const baseFrom = recentFrom - baselineDays * dayMs;

  const med = (xs: number[]): number | null => {
    if (!xs.length) return null;
    const s = [...xs].sort((a, b) => a - b);
    const mid = s.length >> 1;
    return s.length % 2 === 1 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
  };

  const recent = med(points.filter((p) => p.t >= recentFrom && p.t <= now).map((p) => p.v));
  const base = med(points.filter((p) => p.t >= baseFrom && p.t < recentFrom).map((p) => p.v));

  return {
    recentDays,
    baselineDays,
    recentMedian: recent === null ? null : Math.round(recent * 10) / 10,
    baselineMedian: base === null ? null : Math.round(base * 10) / 10,
    deltaPct:
      recent === null || base === null || base === 0
        ? null
        : Math.round(((recent - base) / base) * 1000) / 10,
  };
}
